import React, { useState } from 'react';
import { Input, Label } from 'reactstrap';

import { formatSettingLabel } from '~libs/stringarly';

import Button from '~components/Button';
import Icon, { faCheck, faSave } from '~components/icon';

import { useGetSettingsQuery } from '~lookups/actions/settings';
import {
  useCreateCompanySettingMutation,
  useGetCompanySettingsQuery,
  useUpdateCompanySettingMutation,
} from '~main/actions/companySettings';

interface ComponentProps {
  isActive: boolean;
}

const feature_tag = 'supplierinvoices';

const CompanySettings: React.FC<ComponentProps> = ({ isActive }) => {
  const { data: definitions = [] } = useGetSettingsQuery({ feature_tag }, { skip: !isActive });
  const { data: settings = [], refetch: refetchSettings } = useGetCompanySettingsQuery(undefined, { skip: !isActive });
  const [createSetting] = useCreateCompanySettingMutation();
  const [updateSetting] = useUpdateCompanySettingMutation();

  const [values, setValues] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const handleChange = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async (lookup_setting_id: string, key: string, value: string, isNew: boolean) => {
    setSavingId(lookup_setting_id);
    try {
      if (isNew) {
        await createSetting({ lookup_setting_id, value }).unwrap();
        await refetchSettings();
      } else {
        const setting = settings.find((setting) => setting.setting.id === lookup_setting_id);
        if (setting) await updateSetting({ id: setting.id, value }).unwrap();
      }
    } catch (err) {
      console.error('Failed to save setting:', err);
      // Revert optimistic local value so the UI falls back to server state
      setValues((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="company-settings-form">
      {definitions.map((definition) => {
        const { id, key, name, value_type } = definition;
        const setting = settings.find((setting) => setting.setting.id === id);
        const existingValue = setting?.value ?? '';
        const value = values[key] ?? existingValue;
        const isNew = !setting;
        const isDirty = isNew || value !== existingValue;
        const isBool = value_type === 'bool';
        const label = name || formatSettingLabel(key);

        return (
          <div key={key} className="setting-row">
            <Label for={key}>{label}</Label>
            {isBool ? (
              <div className="d-flex align-items-center gap-2">
                <Input
                  id={key}
                  type="checkbox"
                  checked={value === 'true'}
                  disabled={savingId === id}
                  onChange={({ target }) => {
                    const newValue = target.checked ? 'true' : 'false';
                    handleChange(key, newValue);
                    handleSave(id, key, newValue, isNew);
                  }}
                />
                {savingId === id && <span className="spinner-border spinner-border-sm" aria-label="Saving setting" />}
              </div>
            ) : (
              <>
                <Input id={key} value={value} onChange={({ target }) => handleChange(key, target.value)} />
                <Button
                  onClick={() => handleSave(id, key, value, isNew)}
                  disabled={savingId === id || !isDirty}
                  color={isDirty ? 'success' : 'secondary'}
                  title={isDirty ? 'Save changes' : 'No changes to save'}
                >
                  <Icon icon={isDirty ? faSave : faCheck} size="lg" />
                </Button>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CompanySettings;
